import { take } from 'rxjs/operators';
import { Component, OnInit, ElementRef, ViewChild } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { EChartsOption } from 'echarts';
import { getSelectedLocationIds } from '@app/data/helpers/locationHelpers';
import {
  defaultColour,
  getWaterClarityColour,
  getRiparianColour,
  getWaterColour,
} from '@app/widgets/helpers/rongo-colours';
import { RongoReportingData } from '@app/widgets/helpers/types';

interface WaiMauriData {
  waterColour: string;
  waterClarity: string;
  riparianCoverage: string;
}

@Component({
  selector: 'app-rongo-wai-mauri',
  templateUrl: './rongo-wai-mauri.component.html',
  styleUrls: ['./rongo-wai-mauri.component.scss'],
})
export class WaiMauriComponent implements OnInit {
  @ViewChild('textEl') textEl!: ElementRef;
  @ViewChild('wrapperEl') wrapperEl!: ElementRef;

  chartTitle: string = 'Rongo Wai Mauri';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-wai-mauri';
  startDate: any = '';
  endDate: any = '';
  data: WaiMauriData = { waterColour: 'N/A', waterClarity: 'N/A', riparianCoverage: 'N/A' };
  waiMauriWord: string = '';
  waiMauriWordColour: string = defaultColour;
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  availableOptions: string[] = ['Water Clarity', 'Water Colour', 'Riparian Coverage'];
  selectedOption: string = 'Water Colour';
  site: Site | null = null;
  locationIds: string[] = [];

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService
  ) {}

  ngOnInit() {
    this.locationIds = getSelectedLocationIds(this.filterService.getFilters().selectedLocations);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }

  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService.getWaiMauriData(this.startDate, this.endDate, this.locationIds).subscribe(
      (data: RongoReportingData<WaiMauriData>) => {
        const { config, data: waiMauriData } = data;
        this.chartTitle = config.title;
        this.chartSubtitle = config.subTitle || '';
        this.data = waiMauriData;
        this.setChartData();
      },
      (error: any) => {
        console.error('Error fetching data:', error);
      }
    );
  };

  getWaiMauriWord = (option: string): string => {
    switch (option) {
      case 'Water Clarity':
        return this.data.waterClarity;
      case 'Water Colour':
        return this.data.waterColour;
      case 'Riparian Coverage':
        return this.data.riparianCoverage;
      default:
        return 'N/A';
    }
  };

  getWaiMauriWordColour = (option: string): string => {
    switch (option) {
      case 'Water Clarity':
        return getWaterClarityColour(this.data.waterClarity);
      case 'Water Colour':
        return getWaterColour(this.data.waterColour);
      case 'Riparian Coverage':
        return getRiparianColour(this.data.riparianCoverage);
      default:
        return 'N/A';
    }
  };

  setChartData = () => {
    this.waiMauriWord = this.getWaiMauriWord(this.selectedOption);
    this.waiMauriWordColour = this.getWaiMauriWordColour(this.selectedOption);
    this.noData = this.waiMauriWord === 'N/A';

    // Trigger font-size recalculation
    setTimeout(() => this.fitTextToHeight());
  };

  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };

  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const newLocationIds = getSelectedLocationIds(filters.filters.selectedLocations);
    const locationsChanged = newLocationIds.join(',') !== this.locationIds.join(',');
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || locationsChanged || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.locationIds = newLocationIds;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }

  handleWaiMauriTypeChange(event: MatSelectChange): void {
    this.setChartData();
  }

  private fitTextToHeight() {
    if (!this.textEl || !this.wrapperEl || !this.textEl.nativeElement || !this.wrapperEl.nativeElement) {
      return;
    }

    const wrapperHeight = this.wrapperEl.nativeElement.clientHeight;
    const textElement = this.textEl.nativeElement as HTMLElement;

    let fontSize = 100;
    textElement.style.fontSize = fontSize + 'px';

    while (textElement.scrollHeight > wrapperHeight - 100 && fontSize > 10) {
      fontSize -= 1;
      textElement.style.fontSize = fontSize + 'px';
    }
  }
}
